import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { formatExampleText } from "../../../apiHandles/user_progress"
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import "../../../assets/css/gamesCss/Tree/binaryTreeIntro.css"
import "../../../assets/css/GameField.css"
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";

const correctStructure = ['A', 'B', 'E', 'C', 'NULL', 'D', 'F'];

const EnhancedBinaryTreeBuilder = () => {
    const [treeNodes, setTreeNodes] = useState(Array(7).fill(null));
    const [availableNodes, setAvailableNodes] = useState([]);
    const [draggedNode, setDraggedNode] = useState(null);
    const [message, setMessage] = useState("");
    const [gameState, setGameState] = useState("playing");
    const [lives, setLives] = useState(3);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const { darkMode } = useDarkMode();
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    const mission = selLanguage ? 'Στόχος μας εδώ είναι η κατασκευή ενός ΠΛΉΡΟΥΣ δυαδικού δέντρου. Για την δημιοργία του πρέπει να συνδιάσεις τις παρακάτω λεπτομέρειες του δέντρου. Ο κόμβος Α είναι ρίζα του δέντρου και είναι πατέρας του Β. Ο κόμβος C είναι φύλλο και είναι παιδί του B. O D έχει αδελφό τον F. Ο κόμβος E έχει βάθος 1. Σημείωση: ΠΆΝΤΑ στο αριστερό φύλλο να μπαίνει το γράμμα που είναι πιο νωρίς στην αλφάβητο. Προσοχή! : Πρέπει να συμπληρωθούν όλοι οι κύκλοι.' : "Our goal here is to construct a FULL binary tree. To create it, you must combine the following details: Node A is the root of the tree and is the parent of B. Node C is a leaf and a child of B. D has F as a sibling. Node E has depth 1. Note: The letter that comes earlier in the alphabet must ALWAYS be placed in the left child position. Attention!: All circles must be filled.";

    const handlePlayAgain = () => {
        initializeGame();
    };

    const selectedStage = JSON.parse(localStorage.getItem("selectedStage"));
    var games = selectedStage.games;
    var nameGameKey = localStorage.getItem("gameClicked");
    var selectedGame = null;
    for (let i = 0; i < games.length; i++) {
        if (games[i].game_title === nameGameKey) {
            selectedGame = games[i];
            break;
        }
    }

    useEffect(() => {
        initializeGame();
    }, []);

    const initializeGame = () => {
        const realNodes = ['B', 'E', 'C', 'D', 'F'];
        const fakeNodes = ['G', 'NULL', 'NULL'];
        const mixed = [...realNodes, ...fakeNodes].sort(() => 0.5 - Math.random());


        setTreeNodes(['A', null, null, null, null, null, null]);
        setAvailableNodes(mixed);
        setMessage("");
        setGameState("playing");
        setLives(3);
        setShowGameOver(false);
        setShowGameWon(false);
    };

    const handleDragStart = (e, node) => {
        setDraggedNode(node);
        e.dataTransfer.setData("text/plain", node);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDropOnNode = (index) => (e) => {
        e.preventDefault();
        const node = e.dataTransfer.getData("text/plain");

        if (!node || treeNodes[index]) return;

        // Update tree nodes
        setTreeNodes((prev) => {
            const updated = [...prev];
            updated[index] = node;
            return updated;
        });

        // Remove the node from available nodes (this was the main issue!)
        setAvailableNodes((prev) => prev.filter((n) => n !== node));
    };

    const handleRemoveNode = (index) => {
        const node = treeNodes[index];
        if (!node || node === 'A') return;

        // Remove from tree
        setTreeNodes((prev) => {
            const updated = [...prev];
            updated[index] = null;
            return updated;
        });

        // Add back to available nodes
        setAvailableNodes((prev) => [...prev, node]);
    };

    const checkSolution = () => {
        const isCorrect = correctStructure.every((val, i) => treeNodes[i] === val);
        if (isCorrect) {
            setGameState("won");
            setMessage(selLanguage ? " Μπράβο! Δημιούργησες σωστά το πλήρες δυαδικό δέντρο!" : " Well done! You have correctly created the full binary tree!");
            setShowGameWon(true);
        } else {
            const newLives = lives - 1;
            setLives(newLives);

            if (newLives === 0) {
                setGameState("lost");
                setShowGameOver(true);
                setMessage(selLanguage ? " Έχασες όλες τις ζωές σου. Καλή προσπάθεια." : " You have lost all your lives. Better luck next time.");
            }
            else {
                setMessage(selLanguage ? "Κάποιοι κόμβοι έχουν τοποθετηθεί σε λάθος σημείο. Ξανά προσπάθησε." : " Some nodes are placed incorrectly. Try again.");
            }
        }
    };

    const renderNode = (label, index) => {
        const nodeValue = treeNodes[index];
        return (
            <div
                key={index}
                onDragOver={handleDragOver}
                onDrop={handleDropOnNode(index)}
                onClick={() => handleRemoveNode(index)}
                style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    backgroundColor: nodeValue ? "#3b82f6" : "#e5e7eb",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    cursor: nodeValue && nodeValue !== 'A' ? "pointer" : "default",
                    border: nodeValue ? "2px solid #1d4ed8" : "2px dashed #9ca3af",
                }}
                title={nodeValue && nodeValue !== 'A' ? selLanguage ? "Κλικ για αφαίρεση" : "Click to remove" : ""}
            >
                {nodeValue === 'NULL' ? '∅' : (nodeValue || "")}
            </div>
        );
    };

    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                setMessage("");
            }, 2200);
            return () => clearTimeout(timer);
        }
    }, [message]);

    return (
        <div
            className='main-content binaryIntroContainer'
            style={{
                background: darkMode ? "#1E293B" : "var(--customBuildButton)",
            }}
        >
            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={handlePlayAgain}
            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={handlePlayAgain}
            />

            <div className="max-w-7xl mx-auto" style={{ width: "100%" }}>
                <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginBottom: "5px" }}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "row",
                        justifyContent: "right",
                        gap: "12px"
                    }}>


                        <div style={{
                            display: "flex",
                            gap: "6px",
                            alignItems: "center"
                        }}>
                            {[1, 2, 3].map((heartNum) => (
                                <div
                                    key={heartNum}
                                    style={{
                                        fontSize: "24px",
                                        filter: lives >= heartNum
                                            ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                            : "grayscale(100%) opacity(0.3)",
                                        transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                        transition: "all 0.3s ease",
                                        animation: lives >= heartNum ? "heartbeat 2s ease-in-out infinite" : "none"
                                    }}
                                >
                                    ❤️
                                </div>
                            ))}
                        </div>


                    </div>

                    <style>{`
                        @keyframes heartbeat {
                            0%, 100% { transform: scale(1); }
                            50% { transform: scale(1.1); }
                        }
                    `}</style>
                </div>

                <br />

                <div className="binaryIntroPron">
                    {mission}
                </div>

                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "32px",
                    marginBottom: "24px",
                    maxWidth: "600px",
                    margin: "0 auto 24px auto",
                    width: "100%"
                }}>
                    {/* Level 0 */}
                    <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
                        {renderNode("A", 0)}
                    </div>
                    {/* Level 1 */}
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "clamp(60px, 15vw, 96px)"
                    }}>
                        {renderNode("B", 1)}
                        {renderNode("E", 2)}
                    </div>
                    {/* Level 2 */}
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "clamp(16px, 5vw, 32px)",
                        flexWrap: "wrap"
                    }}>
                        {renderNode("C", 3)}
                        {renderNode("NULL", 4)}
                        {renderNode("D", 5)}
                        {renderNode("F", 6)}
                    </div>
                </div>

                <div className="binaryIntroRemainingNodes">
                    <h3 style={{
                        fontWeight: "600",
                        marginBottom: "16px",
                        color: "white",
                        textAlign: "center"
                    }}>
                        {selLanguage ? "Διαθέσιμοι Κόμβοι:" : "Available Nodes:"}
                    </h3>
                    <div style={{
                        display: "flex",
                        gap: "12px",
                        flexWrap: "wrap",
                        justifyContent: "center"
                    }}>
                        {availableNodes.map((node, idx) => (
                            <motion.div
                                className="binaryTreeNodes"
                                key={`${node}-${idx}`}
                                draggable
                                onDragStart={(e) => handleDragStart(e, node)}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.95 }}
                                title={selLanguage ? `${node} - Σύρε στο δέντρο` : `${node} - Drag to tree`}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                            >
                                {node === 'NULL' ? '∅' : node}
                            </motion.div>
                        ))}
                    </div>
                </div>

                <AnimatePresence>
                    {message && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            style={{
                                textAlign: "center",
                                padding: "20px",
                                borderRadius: "16px",
                                marginBottom: "20px",
                                background: gameState === "won"
                                    ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                                    : "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
                                border: `2px solid ${gameState === "won" ? "#10b981" : "#ef4444"}`,
                                color: gameState === "won" ? "#059669" : "#dc2626",
                                fontWeight: 600,
                                fontSize: "1rem",
                                boxShadow: gameState === "won"
                                    ? "0 10px 25px rgba(16, 185, 129, 0.2)"
                                    : "0 10px 25px rgba(239, 68, 68, 0.2)",
                                maxWidth: "600px",
                                margin: "0 auto 20px auto",
                                width: "100%"
                            }}
                        >
                            {message}
                        </motion.div>
                    )}
                </AnimatePresence>

                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "12px",
                    width: "100%",
                    maxWidth: "400px",
                    margin: "0 auto"
                }}>
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={checkSolution}
                        className="commonGameCheckButton"
                        onMouseEnter={(e) => {
                            e.target.style.boxShadow = "0 15px 35px rgba(59, 130, 246, 0.4), 0 0 0 2px rgba(59, 130, 246, 0.3)";
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.boxShadow = "0 10px 25px rgba(59, 130, 246, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.2)";
                        }}
                    >
                        {selLanguage ? "Έλεγχος" : "Check"}
                    </motion.button>

                    <motion.button
                        className="commonGameDeleteButton"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={initializeGame}
                        onMouseEnter={(e) => {
                            e.target.style.boxShadow = "0 15px 35px rgba(184, 30, 30, 0.4), 0 0 0 2px rgba(184, 30, 30, 0.3)";
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.boxShadow = "0 10px 25px rgba(184, 30, 30, 0.3), 0 0 0 1px rgba(184, 30, 30, 0.2)";
                        }}
                    >
                        {selLanguage ? "Καθαρισμός" : "Reset"}
                    </motion.button>
                </div>
            </div>
        </div>
    );
};

export default EnhancedBinaryTreeBuilder;